/** Section 3 — prizes and odds.
 *  1-in-N decides WIN or LOSE. The weights only decide WHICH prize a winner
 *  gets; weight 0 keeps a prize in the list without ever drawing it. */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, Image as ImageIcon, Loader2, Plus, Save, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { BrandAssetPicker } from '../../voucher_settings/BrandAssetPicker';
import api from '../../../lib/api';
import { PrizeGoalSettings } from './PrizeGoalSettings';
import { getToken } from '../../../lib/session';
import { looksLikeImage, prepareImageForUpload, uploadFailureMessage, uploadWithProgress } from '../../../utils/imageUpload';
import { btn } from './buttons';

const field = "w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm";

const abs = (url) => (!url ? null
    : (url.startsWith('http') ? url : `${(typeof window !== 'undefined' ? window.location.origin : '')}${url}`));

// image_delivery's pre-baked variant: a fixed .webp filename with immutable
// caching, which is the fast path the win screen uses too.
const sized = (url, w) => {
    if (!url) return null;
    const asset = url.match(/^\/api\/brand-assets\/([A-Za-z0-9_-]+)\/serve/);
    if (asset) {
        const ver = url.includes('?') ? url.slice(url.indexOf('?')) : '';
        return `${abs(`/api/brand-assets/${asset[1]}/serve/w${w}.webp`)}${ver}`;
    }
    return `${abs(url)}${url.includes('?') ? '&' : '?'}w=${w}`;
};

const PrizeRow = ({ prize, orgId, promotionId, onChanged, totalWeight, expiryDays = 0,
                   onExpiryDays = null }) => {
    const { t } = useTranslation();
    // false, or the field name of the slot whose Brand library is open.
    const [picker, setPicker] = useState(false);
    const [row, setRow] = useState(prize);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const doubleFileRef = useRef(null);
    useEffect(() => { setRow(prize); }, [prize]);

    const set = (k, v) => setRow((p) => ({ ...p, [k]: v }));

    const patch = async (body) => {
        setBusy(true);
        try {
            await api.patch(`/goal-game/admin/prizes/${prize.id}`, { organization_id: orgId, promotion_id: promotionId || null, ...body });
            await onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail === 'last_active_prize'
                ? 'At least one prize must stay active'
                : 'Could not save the prize');
            setRow(prize);
        } finally { setBusy(false); }
    };

    const remove = async () => {
        if (!window.confirm(`Delete "${prize.title_en}"?`)) return;
        setBusy(true);
        try {
            await api.delete(`/goal-game/admin/prizes/${prize.id}`, { params: { organization_id: orgId, promotion_id: promotionId || undefined } });
            await onChanged();
            toast.success('Prize deleted');
        } catch (e) {
            toast.error(e.response?.data?.detail === 'last_active_prize'
                ? 'At least one prize must stay active'
                : 'Could not delete the prize');
        } finally { setBusy(false); }
    };

    /** Removes the PRIZE only. The gift itself stays in the Gift Generator —
     *  this calls the same delete as above and touches no gift setup. */
    const removeFromGame = async () => {
        if (!window.confirm(t('goalGame.prizeRemoveFromGame'))) return;
        setBusy(true);
        try {
            await api.delete(`/goal-game/admin/prizes/${prize.id}`, { params: { organization_id: orgId, promotion_id: promotionId || undefined } });
            await onChanged();
            toast.success(t('goalGame.prizeRemovedFromGame'));
        } catch (e) {
            toast.error(e.response?.data?.detail === 'last_active_prize'
                ? 'At least one prize must stay active'
                : 'Could not remove the prize');
        } finally { setBusy(false); }
    };

    const weight = Number(row.weight ?? 1);
    const share = totalWeight > 0 && row.active ? Math.round((weight / totalWeight) * 100) : 0;

    // The picture rides the EXACT pipeline the Vouchers gift images use:
    // prepareImageForUpload (iPad HEIC → JPEG, long edge capped) then the
    // shared /api/brand-assets upload, which runs read_image_upload's size and
    // type checks and stores the object. It comes back out through
    // image_delivery — ?w= resizing, AVIF/WebP, ETag — so it loads as fast as
    // a voucher gift image. No second upload or delivery path.
    const pickFile = async (picked, field, name) => {
        if (!picked) return;
        if (!looksLikeImage(picked)) { toast.error('Pick an image file'); return; }
        setUploading(field);
        try {
            const file = await prepareImageForUpload(picked, { maxEdge: 900 });
            const res = await uploadWithProgress({
                url: `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/brand-assets`,
                token: getToken(), file,
                fields: { kind: 'gift', organization_id: orgId,
                          name: `${name} — ${row.title_en || prize.id}` },
            });
            if (!res.ok) { toast.error(uploadFailureMessage(res, (k) => k)); return; }
            await patch({ [field]: res.data.url });
            toast.success(`${name} saved`);
        } catch (_e) {
            toast.error('Could not upload that picture');
        } finally { setUploading(false); }
    };

    /** One picture slot. A prize has two of them: the normal picture, and the
     *  doubled one that is shown instead when the table's star player scored.
     *  Same pipeline, same controls, same Brand library. */
    const PictureSlot = ({ field, label, hint, testid, inputRef, name }) => (
        <div className="space-y-1">
            <p className="text-xs font-medium text-slate-500">{label}</p>
            <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 overflow-hidden flex items-center justify-center shrink-0"
                     data-testid={`${testid}-preview-${prize.id}`}>
                    {row[field]
                        ? <img src={sized(row[field], 440)} alt="" className="w-full h-full object-cover" />
                        : <ImageIcon size={18} className="text-slate-400" />}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <input type="file" accept="image/*" ref={inputRef} className="hidden"
                           onChange={(e) => pickFile(e.target.files?.[0], field, name)}
                           data-testid={`${testid}-input-${prize.id}`} />
                    <button type="button" onClick={() => inputRef.current?.click()}
                            disabled={!!uploading} data-testid={`${testid}-upload-${prize.id}`}
                            className={btn.smallSecondary}>
                        {uploading === field ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />}
                        {row[field] ? 'Replace picture' : 'Upload picture'}
                    </button>
                    <button type="button" onClick={() => setPicker(field)} disabled={!!uploading}
                            data-testid={`${testid}-library-${prize.id}`}
                            className={btn.smallSecondary}>
                        <ImageIcon size={13} /> Brand library
                    </button>
                    {row[field] && (
                        <button type="button" onClick={() => patch({ [field]: '' })}
                                disabled={busy || !!uploading}
                                data-testid={`${testid}-remove-${prize.id}`}
                                className="text-xs font-semibold text-slate-500 hover:text-red-500">
                            Remove
                        </button>
                    )}
                </div>
            </div>
            {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
            <p className="text-[11px] text-slate-400"
               data-testid={`${testid}-library-note-${prize.id}`}>
                {t('goalGame.prizePictureLibraryNote')}
            </p>
        </div>
    );

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2"
             data-testid={`gg-prize-row-${prize.id}`}>
            <div className="grid sm:grid-cols-2 gap-2">
                <input className={field} value={row.title_en || ''} placeholder="Prize name EN"
                       onChange={(e) => set('title_en', e.target.value)}
                       data-testid={`gg-prize-title-en-${prize.id}`} />
                <input className={field} value={row.title_fr || ''} placeholder="Nom du prix FR"
                       onChange={(e) => set('title_fr', e.target.value)}
                       data-testid={`gg-prize-title-fr-${prize.id}`} />
                <input className={field} value={row.win_en || ''} placeholder="Winning message EN (optional)"
                       onChange={(e) => set('win_en', e.target.value)}
                       data-testid={`gg-prize-win-en-${prize.id}`} />
                <input className={field} value={row.win_fr || ''} placeholder="Message gagnant FR (optionnel)"
                       onChange={(e) => set('win_fr', e.target.value)}
                       data-testid={`gg-prize-win-fr-${prize.id}`} />
                <input className={field} value={row.double_en || ''}
                       placeholder={`Doubled EN (default "2 \u00d7 ${row.title_en || 'prize'}")`}
                       onChange={(e) => set('double_en', e.target.value)}
                       data-testid={`gg-prize-double-en-${prize.id}`} />
                <input className={field} value={row.double_fr || ''}
                       placeholder={`Doubl\u00e9 FR (par d\u00e9faut \u00ab 2 \u00d7 ${row.title_fr || 'prix'} \u00bb)`}
                       onChange={(e) => set('double_fr', e.target.value)}
                       data-testid={`gg-prize-double-fr-${prize.id}`} />
            </div>
            <p className="text-[11px] text-slate-500">
                The doubled lines are used only when a table&apos;s star player scores.
                Leave them empty for &quot;2 &times; the prize name&quot;; type your own when
                that would read oddly &mdash; for example &quot;$50 Gift Certificate &times;2&quot;.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
                <PictureSlot field="image_url" testid="gg-prize-image"
                             inputRef={fileRef} name="Prize picture"
                             label={t('goalGame.prizePicture')} />
                <PictureSlot field="double_image_url" testid="gg-prize-double-image"
                             inputRef={doubleFileRef} name="Doubled picture"
                             label={t('goalGame.prizePictureDoubled')}
                             hint={t('goalGame.prizePictureDoubledHint')} />
            </div>

            <BrandAssetPicker
                open={!!picker}
                onOpenChange={(open) => { if (!open) setPicker(false); }}
                kind="gift"
                orgId={orgId}
                token={getToken()}
                isSuperAdmin
                onApply={async (url) => {
                    const field = picker || 'image_url';
                    setPicker(false);
                    await patch({ [field]: url });
                }}
            />

            <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-slate-500">
                    {t('goalGame.prizeKind', 'Kind')}
                    <select className={`${field} w-44`} value={row.kind || 'item'}
                            onChange={(e) => patch({ kind: e.target.value })}
                            data-testid={`gg-prize-kind-${prize.id}`}>
                        <option value="item">{t('goalGame.prizeKindItem', 'Item prize')}</option>
                        <option value="gift_card_next_game">
                            {t('goalGame.prizeKindGiftCard', 'Gift card for the next game')}
                        </option>
                    </select>
                </label>
                {row.kind === 'gift_card_next_game' && (
                    <label className="flex items-center gap-2 text-xs text-slate-500"
                           title="The player must text the card's code from their own phone before staff may accept it">
                        <input type="checkbox" checked={!!row.sms_activation}
                               onChange={(e) => patch({ sms_activation: e.target.checked })}
                               data-testid={`gg-prize-sms-activation-${prize.id}`} />
                        Activate by text
                    </label>
                )}
                {row.kind === 'gift_card_next_game' && (
                    <label className="flex items-center gap-2 text-xs text-slate-500">
                        {t('goalGame.prizeValue', 'Value $')}
                        <input type="number" min={0} max={1000} step="0.5"
                               value={row.value ?? 5}
                               onChange={(e) => set('value', e.target.value)}
                               data-testid={`gg-prize-value-${prize.id}`}
                               className={`${field} w-24 tabular-nums`} />
                    </label>
                )}
                <label className={`flex items-center gap-2 text-xs text-slate-500
                                   ${row.kind === 'gift_card_next_game' ? 'opacity-40' : ''}`}>
                    Weight
                    <input type="number" min={0} max={1000} value={weight}
                           onChange={(e) => set('weight', e.target.value)}
                           data-testid={`gg-prize-weight-${prize.id}`}
                           className={`${field} w-20 tabular-nums`} />
                    <span className="tabular-nums" data-testid={`gg-prize-share-${prize.id}`}>{share}% of wins</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-500">
                    <input type="checkbox" checked={!!row.active}
                           onChange={(e) => patch({ active: e.target.checked })}
                           data-testid={`gg-prize-active-${prize.id}`} />
                    Active
                </label>
                <div className="flex-1" />
                <button type="button" disabled={busy}
                        onClick={() => patch({
                            title_en: row.title_en, title_fr: row.title_fr,
                            win_en: row.win_en || '', win_fr: row.win_fr || '',
                            double_en: row.double_en || '', double_fr: row.double_fr || '',
                            weight: Number(row.weight ?? 1),
                            kind: row.kind || 'item',
                            value: Number(row.value ?? 0),
                        })}
                        data-testid={`gg-prize-save-${prize.id}`}
                        className={btn.smallPrimary}>
                    {busy ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save
                </button>
                <button type="button" onClick={remove} disabled={busy}
                        data-testid={`gg-prize-delete-${prize.id}`}
                        className={btn.iconDanger}>
                    <Trash2 size={14} />
                </button>
            </div>

            {/* The Goal Game settings of this prize — the SAME component the
                gift's own settings screen shows, writing the SAME stored row. */}
            <div className="border-t border-slate-100 dark:border-slate-800 pt-2">
                <PrizeGoalSettings prize={prize} orgId={orgId} promotionId={promotionId}
                                   onChanged={onChanged} showWeight={false}
                                   expiryDays={expiryDays} onExpiryDays={onExpiryDays} />
            </div>
            {row.kind === 'gift_card_next_game' && (
                <p className="text-[11px] text-slate-400" data-testid={`gg-prize-use-fixed-${prize.id}`}>
                    {t('goalGame.prizeUseNextVisitFixed')}
                </p>
            )}
            {row.gift_setup_id && (
                <div className="flex flex-wrap items-center gap-3">
                    <span className="text-[11px] text-slate-500" data-testid={`gg-prize-design-${prize.id}`}>
                        {t('goalGame.prizeFromGiftGenerator',
                           { name: row.gift_name || row.gift_setup_name || row.gift_setup_id })}
                    </span>
                    <button type="button" disabled={busy} onClick={removeFromGame}
                            data-testid={`gg-prize-unlink-${prize.id}`}
                            className="text-[11px] font-semibold text-slate-500 hover:text-red-500">
                        {t('goalGame.prizeRemoveFromGame')}
                    </button>
                </div>
            )}
        </div>
    );
};

/** The nightly limits for gift-card prizes. Super Admin only, per organization.
 *  At a cap nobody wins a card and nothing breaks. */
const GiftCardLimits = ({ orgId, onDays }) => {
    const { t } = useTranslation();
    const [row, setRow] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get('/goal-game/admin/gift-card-settings', { params: { organization_id: orgId } })
            .then((r) => {
                if (!alive) return;
                setRow(r.data.settings);
                onDays?.(Number(r.data.settings?.expiry_days) || 0);
            })
            .catch(() => { if (alive) setRow(null); });
        return () => { alive = false; };
    }, [orgId]);

    if (!row) return null;
    const set = (k, v) => setRow((p) => ({ ...p, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            const r = await api.put('/goal-game/admin/gift-card-settings', {
                organization_id: orgId,
                max_cards_per_night: Number(row.max_cards_per_night) || 0,
                max_value_per_night: Number(row.max_value_per_night) || 0,
                max_per_fan_per_game: Number(row.max_per_fan_per_game) || 0,
                expiry_days: Number(row.expiry_days) || 30,
            });
            setRow(r.data.settings);
            onDays?.(Number(r.data.settings?.expiry_days) || 0);
            toast.success(t('goalGame.giftCardLimitsSaved', 'Gift card limits saved'));
        } catch (_e) {
            toast.error(t('goalGame.giftCardLimitsFailed', 'Could not save the gift card limits'));
        } finally { setBusy(false); }
    };

    const num = (key, label, step) => (
        <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
            <input type="number" min={0} step={step || 1} value={row[key] ?? 0}
                   onChange={(e) => set(key, e.target.value)}
                   data-testid={`gg-giftcard-${key.replace(/_/g, '-')}`}
                   className={`${field} w-28 tabular-nums`} />
        </label>
    );

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3"
             data-testid="gg-giftcard-limits">
            <p className="text-sm font-semibold">
                {t('goalGame.giftCardLimits', 'Gift card for the next game — nightly limits')}
            </p>
            <div className="flex flex-wrap items-end gap-3">
                {num('max_cards_per_night', t('goalGame.giftCardMaxCards', 'Max cards per night'))}
                {num('max_value_per_night', t('goalGame.giftCardMaxValue', 'Max total value per night $'), 5)}
                {num('max_per_fan_per_game', t('goalGame.giftCardMaxPerFan', 'Max per fan per game'))}
                {num('expiry_days', t('goalGame.giftCardExpiryDays', 'Expires after (days)'))}
                <button type="button" onClick={save} disabled={busy}
                        data-testid="gg-giftcard-save" className={btn.primary}>
                    {busy ? <Loader2 className="animate-spin" size={13} /> : null}
                    {t('goalGame.giftCardLimitsSave', 'Save limits')}
                </button>
            </div>
            <p className="text-[11px] text-slate-400" data-testid="gg-giftcard-note">
                {t('goalGame.giftCardNote',
                   'A gift card is won by a player who played from home and picked the winning team. It is emailed to them and can only be used from the end of that game night.')}
            </p>
        </div>
    );
};

/** "Add a gift card" — ONE list of every gift card this venue has already
 *  created, wherever it was made: the saved gifts of EVERY Gift Generator
 *  setup, grouped under each setup's name, and the vouchers made in Voucher
 *  Settings (greyed, with the reason they cannot be won). One tap adds a gift
 *  through the SAME shared add as the gift's own "Add to Goal Game" button
 *  (/goal-game/admin/prizes/from-gift). Anything already in this game says so
 *  instead of being added twice. */
const GiftLibraryPicker = ({ orgId, promotionId, onAdded }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [designs, setDesigns] = useState(null);
    const [vouchers, setVouchers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newFr, setNewFr] = useState('');
    const [newValue, setNewValue] = useState('');
    const [newImage, setNewImage] = useState('');

    const load = async () => {
        try {
            const res = await api.get('/goal-game/admin/gift-library', {
                params: { organization_id: orgId, promotion_id: promotionId || undefined },
            });
            setDesigns(res.data.designs || []);
            setVouchers(res.data.vouchers || []);
        } catch (_e) { setDesigns([]); setVouchers([]); }
    };

    const show = async () => { setOpen(true); await load(); };

    const add = async (setupId, giftId) => {
        setBusy(true);
        try {
            const res = await api.post('/goal-game/admin/prizes/from-gift', {
                organization_id: orgId, promotion_id: promotionId || null,
                setup_id: setupId, gift_id: giftId || null,
            });
            await load();
            await onAdded();
            toast[res.data.created ? 'success' : 'info'](
                t(res.data.created ? 'goalGame.addedToGoalGame' : 'goalGame.alreadyInGoalGame'));
        } catch (_e) {
            toast.error(t('goalGame.addGiftCardFailed'));
        } finally { setBusy(false); }
    };

    /** One row: the picture of its design, the name, the value, and either Add
     *  or "Already added". A row that cannot be won is greyed with its reason. */
    const line = ({ key, name, value, image, inGame, setupId, giftId, testid, reason }) => (
        <div key={key}
             className={`flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 ${reason ? 'opacity-60' : ''}`}
             data-testid={`${testid}-row`}>
            <div className="w-10 h-10 shrink-0 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 overflow-hidden flex items-center justify-center">
                {image ? <img src={sized(image, 160)} alt="" className="w-full h-full object-cover" />
                    : <Gift size={14} className="text-slate-400" />}
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-sm truncate">{name}</p>
                {value > 0 && (
                    <p className="text-[11px] text-slate-500 tabular-nums">${value}</p>
                )}
                {reason && <p className="text-[11px] text-slate-500">{reason}</p>}
            </div>
            {reason ? null : inGame ? (
                <span className="text-[11px] font-semibold text-emerald-600 shrink-0"
                      data-testid={`${testid}-already`}>
                    {t('goalGame.alreadyAdded')}
                </span>
            ) : (
                <button type="button" disabled={busy} onClick={() => add(setupId, giftId)}
                        data-testid={testid} className={btn.smallPrimary}>
                    {busy ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />}
                    {t('goalGame.addToGoalGame')}
                </button>
            )}
        </div>
    );

    const anyGift = (designs || []).some((d) => (d.gifts || []).length > 0);
    const defaultSetup = (designs || []).find((d) => d.is_default) || (designs || [])[0];
    const designImage = defaultSetup?.image_url || null;

    /** CREATE A NEW GIFT CARD, right here: it is saved in this venue's Default
     *  Gift Generator setup (the same save the gift's own "Save gift" button
     *  uses) and added as a prize in this promotion, in one tap. */
    const create = async (event) => {
        event.preventDefault();
        const name = newName.trim();
        if (!name) { toast.error(t('giftGenerator.giftNameRequired')); return; }
        if (!defaultSetup) { toast.error(t('goalGame.giftCreateFailed')); return; }
        setBusy(true);
        try {
            const saved = await api.post(
                `/voucher-settings/gift-setups/${defaultSetup.setup_id}/gifts`,
                {
                    organization_id: orgId, title: name, title_fr: newFr.trim(),
                    gift_type: 'monetary', value: Number(newValue) || 0,
                    // The Goal Game's own caps decide how many are given each
                    // night and per player, so the gift itself is stocked to the
                    // largest number a setup allows.
                    quantity: 100000, image_url: newImage || null,
                });
            await api.post('/goal-game/admin/prizes/from-gift', {
                organization_id: orgId, promotion_id: promotionId || null,
                setup_id: defaultSetup.setup_id, gift_id: saved.data.gift.id,
            });
            setNewName(''); setNewFr(''); setNewValue(''); setNewImage('');
            setCreating(false);
            await load();
            await onAdded();
            toast.success(t('goalGame.giftCreated'));
        } catch (e) {
            toast.error(e.response?.data?.detail === 'gift_name_required'
                ? t('giftGenerator.giftNameRequired') : t('goalGame.giftCreateFailed'));
        } finally { setBusy(false); }
    };

    return (
        <>
            <button type="button" onClick={show} data-testid="gg-prize-add-from-gift"
                    className={btn.primary}>
                <Gift size={14} /> {t('goalGame.addGiftCard')}
            </button>
            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                     onClick={() => setOpen(false)} data-testid="gg-gift-library-modal">
                    <div className="w-full max-w-lg max-h-[80vh] overflow-auto rounded-2xl bg-white dark:bg-slate-900 p-4 space-y-3"
                         onClick={(e) => e.stopPropagation()}>
                        <p className="text-sm font-semibold">{t('goalGame.addGiftCard')}</p>
                        {/* Make one right here — no other page, no second save. */}
                        {!creating ? (
                            <button type="button" onClick={() => setCreating(true)}
                                    data-testid="gg-gift-create-open" className={btn.primary}>
                                <Plus size={14} /> {t('goalGame.createGiftCard')}
                            </button>
                        ) : (
                            <form onSubmit={create} className="space-y-2 rounded-xl border border-slate-200 dark:border-slate-700 p-3"
                                  data-testid="gg-gift-create-form">
                                <div className="grid sm:grid-cols-2 gap-2">
                                    <input className={field} value={newName} maxLength={120}
                                           onChange={(e) => setNewName(e.target.value)}
                                           placeholder={t('goalGame.giftNameEn')}
                                           data-testid="gg-gift-create-name" />
                                    <input className={field} value={newFr} maxLength={120}
                                           onChange={(e) => setNewFr(e.target.value)}
                                           placeholder={t('goalGame.giftNameFr')}
                                           data-testid="gg-gift-create-name-fr" />
                                </div>
                                <div className="grid sm:grid-cols-2 gap-2">
                                    <input className={field} value={newValue} inputMode="decimal"
                                           onChange={(e) => setNewValue(e.target.value.replace(/[^\d.]/g, ''))}
                                           placeholder={t('goalGame.giftValue')}
                                           data-testid="gg-gift-create-value" />
                                    <input className={field} value={newImage}
                                           onChange={(e) => setNewImage(e.target.value)}
                                           placeholder={t('goalGame.giftPicture')}
                                           data-testid="gg-gift-create-image" />
                                </div>
                                {/* The venue's current gift design, unless a picture is given. */}
                                <div className="flex items-center gap-2">
                                    <div className="w-12 h-12 rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
                                        {(newImage || designImage) ? (
                                            <img src={sized(newImage || designImage, 160)} alt=""
                                                 className="w-full h-full object-cover"
                                                 data-testid="gg-gift-create-preview" />
                                        ) : <Gift size={14} className="text-slate-400" />}
                                    </div>
                                    <p className="text-[11px] text-slate-500">
                                        {newImage ? t('goalGame.giftPicture') : t('goalGame.giftPictureDefault')}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button type="submit" disabled={busy} className={btn.primary}
                                            data-testid="gg-gift-create-submit">
                                        {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                                        {t('goalGame.createGiftCardSubmit')}
                                    </button>
                                    <button type="button" onClick={() => setCreating(false)}
                                            className={btn.smallSecondary}
                                            data-testid="gg-gift-create-cancel">
                                        {t('common.close', 'Close')}
                                    </button>
                                </div>
                            </form>
                        )}
                        {designs === null && <Loader2 className="animate-spin" size={16} />}
                        {designs !== null && !anyGift && vouchers.length === 0 && !creating && (
                            <p className="text-sm text-slate-500" data-testid="gg-gift-library-empty">
                                {t('goalGame.giftLibraryEmpty')}
                            </p>
                        )}
                        {/* Every setup of this organization, under its own name. */}
                        {(designs || []).map((d) => (
                            <div key={d.setup_id} className="space-y-1.5">
                                <p className="text-xs font-semibold text-slate-500">{d.name}</p>
                                {(d.gifts || []).map((g) => line({
                                    key: g.gift_id,
                                    name: g.title || t('goalGame.prizeNoTitle', 'Untitled gift'),
                                    value: g.value, image: g.image_url || d.image_url,
                                    inGame: g.in_goal_game, setupId: d.setup_id, giftId: g.gift_id,
                                    testid: `gg-gift-library-add-${g.gift_id}`,
                                }))}
                                {(d.gifts || []).length === 0 && (
                                    <p className="text-[11px] text-slate-500 px-1"
                                       data-testid={`gg-gift-library-setup-empty-${d.setup_id}`}>
                                        {t('goalGame.giftSetupEmpty')}{' '}
                                        {/* Opens Voucher Settings on THIS venue and THIS setup. */}
                                        <a href={`/voucher-settings?organization_id=${orgId}&setup_id=${d.setup_id}`}
                                           className="font-semibold underline"
                                           data-testid={`gg-gift-library-setup-link-${d.setup_id}`}>
                                            {t('goalGame.giftSetupEmptyLink')}
                                        </a>
                                    </p>
                                )}
                            </div>
                        ))}
                        {/* Vouchers made in Voucher Settings — listed, never empty,
                            but greyed with the reason they cannot be won. */}
                        {vouchers.length > 0 && (
                            <div className="space-y-1.5 pt-1">
                                <p className="text-xs font-semibold text-slate-500">
                                    {t('goalGame.voucherGroup')}
                                </p>
                                {vouchers.map((v) => line({
                                    key: v.kind, name: v.name, value: 0, image: null,
                                    testid: `gg-gift-library-voucher-${v.kind}`,
                                    reason: t('goalGame.voucherNotAPrize'),
                                }))}
                            </div>
                        )}
                        <button type="button" onClick={() => setOpen(false)}
                                data-testid="gg-gift-library-close" className={btn.smallSecondary}>
                            {t('common.close', 'Close')}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export const GoalPrizesBlock = ({ cfg, orgId, promotionId, onSave, onReload }) => {
    const { t } = useTranslation();
    const [odds, setOdds] = useState(cfg.odds_one_in);
    const [newEn, setNewEn] = useState('');
    const [newFr, setNewFr] = useState('');
    const [busy, setBusy] = useState(false);
    // How long a next-visit gift card stays valid — the venue's OWN existing
    // setting, read once and shown in plain words on every prize row.
    const [expiryDays, setExpiryDays] = useState(0);

    useEffect(() => { setOdds(cfg.odds_one_in); }, [cfg]);

    const prizes = cfg.prizes || [];
    const totalWeight = prizes
        .filter((p) => p.active)
        .reduce((sum, p) => sum + Number(p.weight ?? 1), 0);

    const addPrize = async () => {
        if (!newEn.trim()) { toast.error('An English name is required'); return; }
        setBusy(true);
        try {
            await api.post('/goal-game/admin/prizes', {
                organization_id: orgId, promotion_id: promotionId || null, title_en: newEn.trim(), title_fr: newFr.trim(), weight: 1, active: true,
            });
            setNewEn(''); setNewFr('');
            await onReload();
            toast.success('Prize added');
        } catch (_e) { toast.error('Could not add the prize'); }
        finally { setBusy(false); }
    };

    const saveOdds = async () => {
        setBusy(true);
        try { await onSave({ odds_one_in: Number(odds) || 1 }); }
        finally { setBusy(false); }
    };

    const pct = (100 / Math.max(1, Number(odds) || 1)).toFixed(1);

    return (
        <div className="space-y-4 pt-3">
            <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                    <span className="block text-xs font-medium text-slate-500 mb-1">Odds — 1 in N</span>
                    <input type="number" min={1} max={1000} value={odds}
                           onChange={(e) => setOdds(e.target.value)} data-testid="gg-odds-input"
                           className={`${field} w-28 tabular-nums`} />
                </label>
                <p className="text-sm text-slate-600 dark:text-slate-300 pb-1.5" data-testid="gg-odds-percent">
                    = <strong>{pct}%</strong> of scratches win
                </p>
                <button type="button" onClick={saveOdds} disabled={busy} data-testid="gg-odds-save"
                        className={btn.primary}>
                    Save odds
                </button>
            </div>

            <GiftCardLimits orgId={orgId} onDays={setExpiryDays} />

            <div className="space-y-2">
                {prizes.map((p) => (
                    <PrizeRow key={p.id} prize={p} orgId={orgId} promotionId={promotionId}
                              onChanged={onReload} totalWeight={totalWeight}
                              expiryDays={expiryDays} onExpiryDays={setExpiryDays} />
                ))}
                {prizes.length === 0 && (
                    <p className="text-sm text-slate-500" data-testid="gg-prizes-empty">No prizes yet.</p>
                )}
            </div>

            <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-3">
                <p className="text-xs font-medium text-slate-500 mb-2">Add a prize</p>
                {/* The gift cards this venue already has come first: one tap
                    each, no typing. */}
                <div className="pb-3">
                    <GiftLibraryPicker orgId={orgId} promotionId={promotionId} onAdded={onReload} />
                </div>
                <p className="text-xs font-medium text-slate-500 mb-2">
                    {t('goalGame.orTypeAPrize', 'Or type a prize of your own')}
                </p>
                <div className="grid sm:grid-cols-2 gap-2 mb-2">
                    <input className={field} value={newEn} onChange={(e) => setNewEn(e.target.value)}
                           placeholder="Prize name EN" data-testid="gg-prize-new-en" />
                    <input className={field} value={newFr} onChange={(e) => setNewFr(e.target.value)}
                           placeholder="Nom du prix FR" data-testid="gg-prize-new-fr" />
                </div>
                <button type="button" onClick={addPrize} disabled={busy} data-testid="gg-prize-add"
                        className={btn.secondary}>
                    <Plus size={14} /> Add prize
                </button>
            </div>
            <p className="text-xs text-slate-500">
                1 in N decides win or lose. The weights only decide which prize a winner gets.
                At least one prize must stay active.
            </p>
        </div>
    );
};

export default GoalPrizesBlock;
